import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { createDefaultPrefs } from '@/shared/domain/prefsDefaults';
import type {
  Favorito,
  ISODateTime,
  PreferenciasConfig,
  PreferenciasServidor,
  WidgetConfig,
} from '@/shared/domain/types';

export type PrefsSyncStatus = 'idle' | 'pending' | 'syncing' | 'error' | 'conflict';

export interface PrefsState {
  config: PreferenciasConfig;
  /** Última modificación local. */
  updatedAt: ISODateTime | null;
  /** El usuario cambió algo respecto de los valores por defecto/perfil. */
  customized: boolean;
  /** Versión del servidor conocida (null = nunca sincronizado). */
  serverVersion: number | null;
  sync: PrefsSyncStatus;
  syncError: string | null;
  /** Copia del servidor mientras hay un conflicto por resolver. */
  conflict: PreferenciasServidor | null;
}

const initialState: PrefsState = {
  config: createDefaultPrefs(),
  updatedAt: null,
  customized: false,
  serverVersion: null,
  sync: 'idle',
  syncError: null,
  conflict: null,
};

const touch = (s: PrefsState) => {
  s.updatedAt = new Date().toISOString();
  s.customized = true;
  if (s.sync === 'idle' || s.sync === 'error') s.sync = 'pending';
};

const prefsSlice = createSlice({
  name: 'prefs',
  initialState,
  reducers: {
    /** Reemplaza todo (perfil aplicado, importación, resolución de conflicto). */
    replaceConfig(state, a: PayloadAction<{ config: PreferenciasConfig; customized?: boolean }>) {
      state.config = a.payload.config;
      state.updatedAt = new Date().toISOString();
      state.customized = a.payload.customized ?? true;
      if (state.sync === 'idle' || state.sync === 'error') state.sync = 'pending';
    },
    patchConfig(state, a: PayloadAction<Partial<PreferenciasConfig>>) {
      state.config = { ...state.config, ...a.payload };
      touch(state);
    },
    setHomeWidgets(state, a: PayloadAction<WidgetConfig[]>) {
      state.config.home.widgets = a.payload;
      touch(state);
    },
    toggleFavorito(state, a: PayloadAction<Omit<Favorito, 'creado'> & { creado?: string }>) {
      const f = a.payload;
      const idx = state.config.favoritos.findIndex((x) => x.id === f.id);
      if (idx >= 0) state.config.favoritos.splice(idx, 1);
      else state.config.favoritos.push({ ...f, creado: f.creado ?? new Date().toISOString() });
      touch(state);
    },
    resetPrefs(state) {
      state.config = createDefaultPrefs();
      state.customized = false;
      state.updatedAt = new Date().toISOString();
      if (state.sync === 'idle' || state.sync === 'error') state.sync = 'pending';
    },
    /** Hay cambios locales sin subir (p. ej. el usuario editó mientras se subía la versión anterior). */
    markSyncPending(state) {
      if (state.sync === 'idle' || state.sync === 'error') state.sync = 'pending';
    },
    syncStarted(state) {
      state.sync = 'syncing';
      state.syncError = null;
    },
    syncSucceeded(state, a: PayloadAction<{ version: number }>) {
      state.serverVersion = a.payload.version;
      state.sync = 'idle';
      state.syncError = null;
      state.conflict = null;
    },
    syncFailed(state, a: PayloadAction<string>) {
      state.sync = 'error';
      state.syncError = a.payload;
    },
    syncConflict(state, a: PayloadAction<PreferenciasServidor>) {
      state.sync = 'conflict';
      state.conflict = a.payload;
    },
    /** Al cerrar sesión se conserva la config local pero se corta el vínculo con el servidor. */
    detachFromServer(state) {
      state.serverVersion = null;
      state.sync = 'idle';
      state.syncError = null;
      state.conflict = null;
    },
  },
});

export const {
  replaceConfig,
  patchConfig,
  setHomeWidgets,
  toggleFavorito,
  resetPrefs,
  markSyncPending,
  syncStarted,
  syncSucceeded,
  syncFailed,
  syncConflict,
  detachFromServer,
} = prefsSlice.actions;
export const prefsReducer = prefsSlice.reducer;
